import _ from "lodash";
import checkAvsStatusError from "~/assets/utils/Avs/checkAvsStatusError";
import getAvsParams, {
  isRegionEntry,
  stripAddressTail,
} from "~/assets/utils/getAvsParams";
import resolveStateCountryObjects from "~/assets/utils/resolveStateCountryObjects";
import { getStore } from "~/assets/utils/storeAccessor";
import updateDetails from "~/assets/utils/updateDetails";
import { AVSStatus, AVSSubStatus } from "~/entities/AVS";
import { Details } from "~/entities/Details";
import { Country, State } from "~/entities/Response/FormData";

// import checkIfDetailUpdated from '~/assets/utils/checkIfDetailUpdated';

export interface IChangedFlags {
  isAddress1Changed: boolean;
  isAddress2Changed: boolean;
  isCityChanged: boolean;
  isCountryChanged: boolean;
  isPostalChanged: boolean;
  isStateChanged: boolean;
}

// BIK-2107: for the region-paste (one-line) path, "no results returned" means the
// pasted text was too incomplete to resolve (not "inactive for delivery"), so the
// user needs to complete the segmented fields. Reclassify it accordingly; all other
// failures keep their normal classification.
function classifyAvsFailure(avsCheck: any, isRegionPaste: boolean): AVSSubStatus {
  const subStatus = checkAvsStatusError(avsCheck);
  if (isRegionPaste && subStatus === AVSSubStatus.noResultsReturned) {
    return AVSSubStatus.addressIncomplete;
  }
  return subStatus;
}

// BIK-2136/2137: the country's states as loaded from /api/FormData/states. The list only
// covers 18 countries, so "no rows" is the normal answer for most of the world and the
// callers below treat it as "this country has no state concept we can validate against".
function statesForCountry(
  store: any,
  countryCode?: string
): Array<{ stateprovcode?: string; stateprov?: string }> {
  if (!countryCode) return [];
  const allowedStates = store?.getters?.["formData/allowedStates"] as Array<{
    country?: string;
    stateprovcode?: string;
    stateprov?: string;
  }> | null;
  if (!allowedStates?.length) return [];
  return allowedStates.filter((s) => s?.country === countryCode);
}

// BIK-2136: is this state value one the country actually has? Compared against both the
// code and the name, since the validator may answer with either ("LH" vs "Louth").
function matchesKnownState(
  store: any,
  value: string,
  countryCode?: string
): boolean {
  const needle = value.trim().toUpperCase();
  if (!needle) return false;
  return statesForCountry(store, countryCode).some(
    (s) =>
      s.stateprovcode?.trim().toUpperCase() === needle ||
      s.stateprov?.trim().toUpperCase() === needle
  );
}

export default async function (details: Details): Promise<{
  stageDetails: Details;
  updatedDetails: Details;
  changedFlags: IChangedFlags;
}> {
  // call avs endpoint to see if it can add to the details on every stage from an to
  // let needsAVSModal = false;
  let changedFlags: IChangedFlags = {
    isAddress1Changed: false,
    isAddress2Changed: false,
    isCityChanged: false,
    isCountryChanged: false,
    isPostalChanged: false,
    isStateChanged: false,
  };
  let stageDetails: Details = _.cloneDeep(details);
  let updatedDetails: Details = _.cloneDeep(details);
  let error: any;
  const store = getStore();
  const NeedsAVSCheck = !stageDetails?.passedAvs && !stageDetails?.avsChecked;
  // BIK-2095: single-input path. When a one-line address was pasted or typed into the main
  // input (flag on), verify it directly via the AVS `region` param (UPS XAV Region mode)
  // instead of the segmented fields. This keeps the entered value intact — no Google Places.
  const avsRawPasteDirect = (store as any)?.$config?.avsRawPasteDirect === true;
  const isRegionPaste = avsRawPasteDirect && isRegionEntry(stageDetails);
  // BIK-2136: needed both by the guard below and by the post-response state handling.
  const detailCountryCode =
    typeof stageDetails.country === "string"
      ? stageDetails.country
      : stageDetails.country?.code;
  if (!isRegionPaste) {
    // BIK-2125: the incomplete-paste fallback (BIK-2107) seeds stageDetails.state/country
    // as empty-but-truthy objects ({ stateprovcode: "", ... }) so the segmented UI doesn't
    // break. A plain `!stageDetails.state` check treats that placeholder object as "present"
    // and lets AVS run with an empty state/country. Check the actual code value instead.
    const stateCode =
      typeof stageDetails.state === "string"
        ? stageDetails.state
        : stageDetails.state?.stateprovcode;
    const countryCode =
      typeof stageDetails.country === "string"
        ? stageDetails.country
        : stageDetails.country?.code;
    // BIK-2137: state was required for EVERY country, but the state dropdown only
    // renders for the countries present in /api/FormData/states (18 today — US, CA,
    // IE, IT, DE, FR, BR, AU...). For anywhere else (Portugal, Taiwan, Spain, ~100
    // more) the field can never be filled, so this guard returned early and AVS was
    // NEVER called: avsChecked stayed false, avsUiState returned "idle", and the
    // address showed neither a green badge nor an error while Search still returned
    // rates. That is the "INT returns rates without Verified" report.
    //
    // ServiceObjects (the non-US validator) does not need it — verified against dev:
    // Portugal, Taiwan, Spain and Germany all come back passedAvs=true with state
    // sent empty, and SO fills the state in from its own data.
    //
    // Require it only where the form can actually supply it: the country has states
    // to choose from, so an empty field means the user skipped a question we did ask.
    // Keying on US alone would have silently stopped asking in the other 17 countries
    // that do have a dropdown today. US stays required even if the list fails to load.
    // Gated on the flag so flag-OFF keeps the previous condition exactly.
    const stateRequired = avsRawPasteDirect
      ? countryCode === "US" ||
        statesForCountry(store, countryCode).length > 0
      : true;
    if (
      !stageDetails.address1 ||
      !stageDetails.streetAddress ||
      !stageDetails.city ||
      !stageDetails.postalCode ||
      !countryCode ||
      (stateRequired && !stateCode)
    ) {
      return {
        stageDetails,
        updatedDetails,
        changedFlags,
      };
    }
  }
  if (NeedsAVSCheck) {
    // BIK-2127: pass the flag so the params match this function's own isRegionPaste
    // decision. Without it getAvsParams re-derived Region from inputMethod/rawInput alone
    // and sent `?region=` even with the flag OFF, while the seeding below (gated on
    // isRegionPaste) was skipped.
    const avsCheck = await store.dispatch(
      "ship/requestAVSCheck",
      getAvsParams(stageDetails, avsRawPasteDirect)
    );
    // BIK-2095: for the region-paste path the detail starts with no segmented
    // state/country objects, so updateDetails would drop the parsed state/country from
    // ValidAddress. Seed empty objects so the UPS-parsed values land on the detail.
    if (isRegionPaste) {
      if (!stageDetails.state) {
        stageDetails.state = {
          country: null,
          id: "",
          stateprov: "",
          stateprovcode: "",
        } as Details["state"];
      }
      if (!stageDetails.country) {
        stageDetails.country = {
          active: true,
          code: "",
          id: "",
          name: "",
        } as Details["country"];
      }
      // A verified one-line paste is a complete address, same as a resolved Google
      // full-address selection. Set searchType so downstream validators (which assume
      // Places always set it) don't hit undefined.searchType.
      if (!stageDetails.searchType) {
        stageDetails.searchType = "full";
      }
    }
    // BIK-2136: the international validator answers with its own spelling of the state,
    // which is not the value we asked about and not one we can use:
    //   - Ireland: user picks "LH" from the dropdown, SO replies "Co. Louth". Same county,
    //     different wording — but it counted as a changed address, so the modal opened
    //     asking the user to confirm a change that never happened, and the long name
    //     overwrote the 2-char code that Global Checkout needs.
    //   - Portugal/Taiwan: no dropdown exists, so SO's state ("Setubal") is a value the
    //     user never entered. Per Travis, state is only meant to reach UPS for US/CA, and
    //     keeping it here leaked it into the rates payload.
    // In both cases the right answer is to ignore SO's state for non-US countries: drop it
    // before updateDetails writes it and before the changed-flags are read, so the address
    // the user gave stands. A state we CAN resolve against the country's own list is kept —
    // that is a real correction. Street/city/postal changes are untouched and still open
    // the modal. US keeps the UPS-returned state exactly as before.
    // BIK-2144: resolve the country from the AVS ANSWER first, falling back to the detail.
    // On the region-paste path the detail's country is still the empty placeholder seeded
    // below (code: ""), because only this response tells us what country the pasted text
    // was. Reading the detail alone meant "" !== "US" for a Texas address, so the guard
    // treated it as international and blanked a state UPS had correctly returned — the
    // address then reached /api/Rates with State: "" and was rejected (400).
    const effectiveCountryCode =
      avsCheck?.validAddress?.countryCode ||
      avsCheck?.originalAddress?.countryCode ||
      detailCountryCode;
    if (
      avsRawPasteDirect &&
      avsCheck?.validAddress &&
      effectiveCountryCode !== "US"
    ) {
      const returned = avsCheck.validAddress.state;
      if (returned && !matchesKnownState(store, returned, effectiveCountryCode)) {
        avsCheck.validAddress.state = "";
        if (avsCheck.changedFlags) {
          avsCheck.changedFlags.isStateChanged = false;
          // isChanged is the OR of the individual flags; recompute it so a state-only
          // "change" no longer opens the modal on its own.
          avsCheck.isChanged = Object.values(
            avsCheck.changedFlags as Record<string, boolean>
          ).some(Boolean);
        }
      }
    }
    // needs to add to the details the residential mark if the avs return true on that
    if (avsCheck && typeof avsCheck.passedAvs === "boolean") {
      if (avsCheck.isChanged) {
        const updatedDetailFrom = _.cloneDeep(stageDetails);
        updatedDetails = updateDetails(avsCheck, updatedDetailFrom);
        // BIK-2134: a region paste arrives as a one-line rawInput with no segmented fields,
        // so on this branch (which keeps the corrected values in updatedDetails only)
        // stageDetails would keep empty city/state/postalCode. IsAddressFilled then reports
        // the address as missing — red field, blocked step — even though AVS passed. Apply
        // the AVS result to stageDetails as well, then put back the street the user typed:
        // that is the field the modal diffs, and the one UPS rewrote.
        if (isRegionPaste) {
          // Only the street, not the whole one-liner — the modal renders city/state/zip on a
          // line of their own, so keeping the tail here would print them twice.
          const entered = stripAddressTail(
            stageDetails.streetAddress ||
              ((stageDetails.rawInput as string) ?? ""),
            {
              city: avsCheck.validAddress?.city,
              state: avsCheck.validAddress?.state,
              postalCode: avsCheck.validAddress?.postalCode,
            }
          );
          stageDetails = updateDetails(avsCheck, stageDetails);
          stageDetails.streetAddress = entered;
          stageDetails.address1 = entered;
        }
        // BIK-2134: the server now returns real flags for region requests too. Fall back to
        // a street-level change rather than an empty object if they are ever missing — an
        // all-false object would silently suppress the modal, which is the very failure this
        // ticket is about.
        changedFlags = avsCheck.changedFlags ?? {
          ...changedFlags,
          isAddress1Changed: true,
        };
        stageDetails.passedAvs = avsCheck.passedAvs;
        if (stageDetails.avsStatus) {
          stageDetails.avsStatus.status = avsCheck.passedAvs
            ? AVSStatus.success
            : AVSStatus.failed;
          if (!avsCheck.passedAvs) {
            stageDetails.avsStatus.subStatus = classifyAvsFailure(
              avsCheck,
              isRegionPaste
            );
            stageDetails.avsProblem = true;
            error = avsCheck.errorMessage;
          }
        }
      } else {
        stageDetails = updateDetails(avsCheck, stageDetails);
        updatedDetails = stageDetails;
        if (avsCheck.passedAvs) {
          stageDetails.avsStatus.status = AVSStatus.success;
        } else {
          stageDetails.avsStatus.status = AVSStatus.failed;
          stageDetails.avsStatus.subStatus = classifyAvsFailure(
            avsCheck,
            isRegionPaste
          );
          stageDetails.avsProblem = true;
          error = avsCheck.errorMessage;
        }
      }
    }
    // BIK-2127: the step 1 unit-required check was gated behind AVS_SHIP1_REWORK and
    // never ran in any environment. It flipped an address that had just PASSED AVS to
    // passedAvs=false, which is the false-negative class BIK-2111 tracks, so it is
    // removed rather than migrated. The apt/unit warning still comes from step 3's own
    // IsUnitRequired call (checkIfAVSRequireAddress2 in steps/Details.vue), untouched.
  }
  // BIK-2144: the region-paste path above seeds an empty country/state placeholder so
  // updateDetails has somewhere to deposit the UPS-parsed values. UPS answers with the CODE
  // only — `id`/`name`/`label` are dropdown fields, not API fields — so the detail is left
  // holding { active: true, code: "US", id: "", name: "" }. That is a valid country as far as
  // AVS and the rates payload are concerned, which is why this went unnoticed, but the
  // Country select renders `use-id` off `.id` and therefore displayed BLANK.
  //
  // Swap the partial placeholder for the real option object now that the code is known.
  // Resolving here (rather than only in the component) means every consumer of the returned
  // details gets the complete object, including the ones that never re-render a select.
  // resolveStateCountryObjects only assigns on a match, so a country we cannot find in the
  // allowed list is left exactly as-is.
  const countryList = store?.getters?.["formData/allowedCountries"] as
    | Country[]
    | null;
  const stateList = store?.getters?.["formData/allowedStates"] as State[] | null;
  resolveStateCountryObjects(stageDetails, countryList, stateList);
  if (updatedDetails && updatedDetails !== stageDetails) {
    resolveStateCountryObjects(updatedDetails, countryList, stateList);
  }
  // stageDetails: the details that are going to be used to check if the avs is needed
  // updatedDetails: the details updated with the avs result, if the AVS modal is needed
  // needsAVSModal: if the AVS modal is needed
  const avsObject: {
    stageDetails: Details;
    updatedDetails: Details;
    changedFlags: IChangedFlags;
    error?: any;
  } = {
    stageDetails,
    updatedDetails,
    changedFlags,
    error,
  };
  return avsObject;
}
